/**
 * MonthlyHoursEditor Component
 * 
 * Allows editing monthly working hours for employees with "monthly" working time model
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Calendar, Save, Loader2, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

export default function MonthlyHoursEditor({ employeeId, weeklyHours = 40 }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthlyHours, setMonthlyHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const loadMonthlyHours = useCallback(async () => {
    if (!employeeId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/employees/${employeeId}/monthly-hours`, {
        params: { year }
      });
      
      const hoursMap = {};
      response.data.forEach(entry => {
        hoursMap[entry.month] = entry.target_hours;
      });
      setMonthlyHours(hoursMap);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading monthly hours:', error);
      toast.error('Fehler beim Laden der monatlichen Arbeitszeiten');
    } finally {
      setLoading(false);
    }
  }, [employeeId, year]);

  useEffect(() => {
    loadMonthlyHours();
  }, [loadMonthlyHours]);

  const handleHoursChange = (month, value) => {
    setMonthlyHours(prev => ({
      ...prev,
      [month]: value === '' ? '' : parseFloat(value) || 0
    }));
    setHasChanges(true);
  };

  const saveAllMonths = async () => {
    setSaving(true);
    try {
      const hoursList = Object.entries(monthlyHours)
        .filter(([_, hours]) => hours !== '' && hours !== undefined)
        .map(([month, hours]) => ({
          employee_id: employeeId,
          year: year,
          month: parseInt(month),
          target_hours: parseFloat(hours)
        }));

      if (hoursList.length === 0) {
        toast.info('Keine Änderungen zu speichern');
        setSaving(false);
        return;
      }

      await api.post(`/employees/${employeeId}/monthly-hours/bulk`, hoursList);
      toast.success('Monatliche Arbeitszeiten gespeichert');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving monthly hours:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const fillAllWithWeekly = () => {
    const monthlyFromWeekly = Math.round(weeklyHours * 4.33 * 10) / 10;
    const filled = {};
    for (let m = 1; m <= 12; m++) {
      filled[m] = monthlyFromWeekly;
    }
    setMonthlyHours(filled);
    setHasChanges(true);
  };

  const calculateYearlyTotal = () => {
    return Object.values(monthlyHours)
      .filter(h => h !== '' && h !== undefined)
      .reduce((sum, h) => sum + parseFloat(h), 0);
  };

  if (loading) {
    return (
      <Card className="border-border/60">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <Skeleton key={`skel-${i}`} className="h-20" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/60 mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Monatliche Arbeitszeiten
            </CardTitle>
            <CardDescription>
              Soll-Stunden pro Monat für variable Arbeitszeiten
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setYear(y => y - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Badge variant="secondary" className="text-lg px-4 py-1">
              {year}
            </Badge>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setYear(y => y + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Actions */}
        <div className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={fillAllWithWeekly}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Alle Monate mit {Math.round(weeklyHours * 4.33)}h füllen
            </Button>
            <span className="text-sm text-muted-foreground">
              (basierend auf {weeklyHours}h/Woche × 4,33)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              Jahresgesamt: <span className="text-primary">{calculateYearlyTotal().toFixed(1)}h</span>
            </span>
          </div>
        </div>

        {/* Month Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {MONTHS.map((monthName, index) => {
            const month = index + 1;
            const value = monthlyHours[month];
            const isEmpty = value === '' || value === undefined;
            
            return (
              <div 
                key={month}
                className={`p-3 rounded-lg border ${isEmpty ? 'bg-muted/30' : 'bg-primary/5 border-primary/20'}`}
              >
                <Label className="text-xs font-medium text-muted-foreground">
                  {monthName}
                </Label>
                <div className="mt-1 flex items-center gap-1">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="300"
                    value={value ?? ''}
                    onChange={(e) => handleHoursChange(month, e.target.value)}
                    className="h-9 text-center"
                    placeholder="-"
                    data-testid={`monthly-hours-${month}`}
                  />
                  <span className="text-xs text-muted-foreground">h</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Save Button */}
        {hasChanges && (
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={saveAllMonths}
              disabled={saving}
              data-testid="save-monthly-hours-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Änderungen speichern
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
